import "reflect-metadata";

import "assets/css/reset.css";
import "assets/css/fonts.css";
import "assets/css/core.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import * as Sentry from "@sentry/react";
import {
	breadcrumbsIntegration,
	browserTracingIntegration,
	init,
	replayIntegration,
} from "@sentry/react";

import React, {Suspense} from "react";
import reportWebVitals from "./reportWebVitals";
import {RootRoutes, SecretGateRoutes} from "routes";
import {BrowserRouter} from "react-router-dom";
import {createRoot} from "react-dom/client";
import {
	controllers,
	DIContainer,
	providers,
	services,
	stores,
} from "data/services/locator/locator_container.service";
import {InjectionProvider} from "data/services/locator/locator_provider.service";
import {StyledEngineProvider} from "@mui/material/styles";
import {ThemeProvider} from "@mui/material";
import {SENTRY_DSN_URL} from "data/constants";
import {PagePreloader} from "views/components/preloader";
import {SecretGateController} from "views/controllers/secret_gate/secret_gate.controller";
import {Session} from "views/components/session/session.component";
import {Bootstrap} from "views/components/bootstrap/bootstrap.component";
import {ModalError} from "views/components/modals/modal_error/modal_error.component";
import {theme} from "assets/css/theme";
import {prepareLanguageForRouter} from "data/utils/language";
import {ModalSecondaryRegistration} from "views/components/modals/modal_secondary_registration/modal_secondary_registration.component";
import {ModalSaveLocalPrediction} from "views/components/modals/modal_save_local_prediction/modal_save_local_prediction.component";
import {ContextualPromptBanners} from "views/components/contextual_prompt_banner/contextual_prompt_banner.component";
import {ModalShare} from "views/components/modals/modal_share/modal_share.component";

let errorCount = 0;
const MAX_ERRORS = 100;
const IGNORE_ERRORS = [
	"Authentication Required",
	"Load failed",
	"UET is not defined",
	"Loading chunk",
	"AbortError: Share canceled",
	"Failed to fetch",
	"Network Error",
	"Minified Redux error",
	"Unexpected end of script",
	"Abort due to cancellation of share",
	"The network connection was lost.",
	"Maximum call stack size exceeded",
	"Possible side-effect in debug-evaluate",
	"Native bridge was not set",
	"Initialization Error: Native bridge was not set",
	"UnhandledRejection: Non-Error promise rejection captured with keys: message, status",
	"Non-Error promise rejection captured with keys: message, status",
	"TypeError: undefined is not an object (evaluating 'ceCurrentVideo.currentTime')",
	"Identifier MlbJavascriptDataStore has already been declared",
	"Identifier 'MlbJavascriptDataStore' has already been declared",
	"SyntaxError: Identifier MlbJavascriptDataStore has already been declared",
	"SyntaxError: Identifier 'MlbJavascriptDataStore' has already been declared",
	"Java exception was raised during method invocation",
];

if (SENTRY_DSN_URL) {
	init({
		dsn: SENTRY_DSN_URL,
		ignoreErrors: IGNORE_ERRORS,
		sampleRate: 0.05,
		allowUrls: [".loc", ".fanhubmedia.com", ".geniussports.com", "www.mlb.com", ".mlb.com"],
		integrations: [
			browserTracingIntegration(),
			replayIntegration(),
			breadcrumbsIntegration({
				console: false,
			}),
		],
		environment: process.env.REACT_APP_SENTRY_ENV || "development",
		beforeSend: (event: Sentry.Event) => {
			errorCount++;
			if (errorCount > MAX_ERRORS) {
				return null;
			}
			if (event && event.message && IGNORE_ERRORS.includes(event.message)) {
				return null;
			}
			return event;
		},
		denyUrls: [
			"quantcast",
			"xsca",
			// browser's extensions
			/extensions\//i,
			/^chrome:\/\//i,
			/^moz-extension:\/\//i,
		],
	});
}

const root = document.getElementById("root");
const baseUrl = prepareLanguageForRouter();

if (!root) {
	throw Error("Root element was not found");
}

DIContainer.load(services, providers, stores, controllers);

createRoot(root).render(
	<React.StrictMode>
		<StyledEngineProvider injectFirst>
			<ThemeProvider theme={theme}>
				<InjectionProvider container={DIContainer}>
					<Bootstrap>
						<BrowserRouter basename={baseUrl}>
							<Suspense fallback={<PagePreloader />}>
								{SecretGateController.IS_SECRET_PASSED ? (
									<Session>
										<RootRoutes />
										<ContextualPromptBanners />
									</Session>
								) : (
									<SecretGateRoutes />
								)}
							</Suspense>
						</BrowserRouter>
						<ModalShare />
						<ModalError />
						<ModalSecondaryRegistration />
						<ModalSaveLocalPrediction />
					</Bootstrap>
				</InjectionProvider>
			</ThemeProvider>
		</StyledEngineProvider>
	</React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
